/**
 * next/image shim for Vite
 *
 * The Next.js app already sets `images: { unoptimized: true }` in next.config.mjs,
 * so this shim is a direct drop-in replacement — a plain <img> element.
 */
import { forwardRef, type ComponentPropsWithoutRef } from 'react'

interface ImageProps extends Omit<ComponentPropsWithoutRef<'img'>, 'src'> {
  src: string | { src: string; height?: number; width?: number }
  alt: string
  width?: number
  height?: number
  /** Fill the parent container (position: absolute, inset 0) */
  fill?: boolean
  /** Ignored — no preloading in Vite */
  priority?: boolean
  /** Ignored — no optimization in Vite */
  quality?: number
  placeholder?: 'blur' | 'empty'
  blurDataURL?: string
  loader?: unknown
  unoptimized?: boolean
}

const Image = forwardRef<HTMLImageElement, ImageProps>(function Image({
  src,
  fill,
  priority: _priority,
  quality: _quality,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  loader: _loader,
  unoptimized: _unoptimized,
  style,
  ...rest
}, ref) {
  const srcStr = typeof src === 'object' && 'src' in src ? src.src : src

  const fillStyle: React.CSSProperties = fill
    ? { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }
    : {}

  // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
  return <img ref={ref} src={srcStr} style={{ ...fillStyle, ...style }} {...rest} />
})

export default Image
